import { dateTimeUtils } from "../dateTimeUtils_v2.js";
import { identityService } from "../profile/identityService_v2.js";
import { utilsApp } from "../utils-app_v2.js";
import { scoreBoard } from "../scoreBoard/scoreBoard_v2.js";
import { board } from "../game/gameBoard_v2.js";
import { zoom } from "../zoom_v2.js";
import {
  ACTIONS,
  PLAYERS,
  DIRECTIONS,
  AUDIENCE_PARTICIPATION,
  VISIBILITY,
  MODE,
  MATCH_STATES,
  MATCH_JOIN_TYPES,
  COMPUTER_NAME,
  COMPUTER_USERID,
  MESSAGE_MAX_LENGTH,
  TROUBLESHOOTING,
} from "../constants_v2.js";
import { sanitizeText } from "../textFilter_v2.js";
import { router } from "../router_v2.js";
import { idem } from "../idem.mjs";
import { app } from "../app_v2.js";
import { mouse } from "../game/mouse_v2.js";

class MatchServiceError extends Error {
  constructor(message, status, errorType = null, matchID = null) {
    super(message);
    this.status = status;
    this.errorType = errorType; // Will be null if not provided
    this.matchID = matchID; // Will be null if not provided
    this.name = "MatchServiceError";
  }
}

const matchState = {
  cache: [], // all matches this session has encountered
  currentMatchID: "notSet", // the one that's rendered on the screen
};

/**
 * The type definition of a match
 * @typedef {object} match
 * @property {matchID} matchID -the matchID of a match. The intent is the user never needs to know this
 * * 7 characters long.
 * * 6 of the characters are upper case letters, except the letter "O".
 * * 1 character is an integer from 1-9, which does not include the integer 0.
 * * The integer can occur in any position.
 * @property {string} state -
 * @property {name} winnerName the full name of the winner (because what if that person's account gets deleted?)
 * @property {userID} ownerUserID - The userID of the owner
 * @property {name} ownerName the fullname of the owner. this identity is also usually a player, but doens't have to be. Could be a person organizing a tournament, for example.
 * @property {name} p1Name -the full name of the player who goes first. Could be "waiting"
 * @property {userID} p1UserID - The user ID of the player who goes first. Could be "waiting"
 * @property {number} p1Score - player one's current real time score
 * @property {userID} turn - the user ID of whose turn is it. Could be "waiting"
 * @property {name} p2Name - the full name of the player who goes second. Could be "waiting"
 * @property {userID} p1UserID - The user ID of the player who goes first. Could be "waiting"
 * @property {number} p2Score - player two's current real time score
 * @property {string} gotWatchers - literally Y or N . used a PK for a GSI to query watchers.
 * @property {[userID]} watchers - attribute only exists if there are watchers. It's an array of userID's who are actually watching the match
 * @property {string} playerCodeP1 - allows identities to assume the role of opponent who is playing first i.e. "playsSecond-SDFR5GM"
 * @property {string} playerCodeP2 - allows identities to assume the role of opponent who is playing second i.e. "playsFirst-SDFR5GM"
 * @property {string} watcherCode - allows identities to assume the role of watcher: e.g 1)  "watcher-F6JXEWG"
 * @property {string} visibility - "invited" or "everyone"
 * @property {timeStamps} created - When the match was created
 * @property {timeStamps} started - When the match was started
 * @property {timeStamps} ended - When the match was ended
 * @property {number} lastMove - the epoch time of the last move (used for sorting)
 * @property {[move]} moves - an array of the moves
 * @property {number} ttl - when created, matches are in "pending" state with 48 hours TTL. When it moves from pending to started, the TTL is removed.
 * @property {boolean } p1GotMovesRotate - Does playerOne currently have any pieces available to rotate?
 * @property {boolean } p1GotMovesPush - Does playerOne currently have any pieces available to push?
 * @property {boolean } p2GotMovesRotate - Does playerTwo currently have any pieces available to rotate?
 * @property {boolean } p2GotMovesPush - Does playerTwo currently have any pieces available to push?
 * @property {[piece]} pieces - the currrent real time set of pieces on the board
 * @property {string} audienceParticipation "watchers" , "everyone", "disabled"
 * @property {string} mode - "sameDevice", "ownDevice"
 * @property {string} message - optional message from the match creator to the opponent (max 512 chars)
 **/

// matchID type
/**
 * @typedef {string} matchID - - seven characters: [0-9,A-Z]
 */

/**
 * @typedef {object} timeStamps - Contains timeStamps in easily retreivable formats
 * @property {number} epoch - Number of seconds since 1/1/1970
 * @property {number} year - the year in four digits: YYYY
 * @property {number} month - the month in two digits: MM
 * @property {number} day - the day in two digits: DD
 */

/**
 * @typedef {object} move - contains one move from the player whose turn it is
 * @property {number} moveEpoch - epoch time stamp (seconds). tells you the order, and probably gonna be useful for analysis
 * @property {string} player - who made the mode e.g. literally "playerOne" or "playerTwo" (you can look up their ID above)
 * @property {number} p1Score - player one's score at the end of this turn
 * @property {number} p2Score - player two's score at the end of this turn
 * @property {string} action e.g "push" or "rotate"
 * @property {coords} coords the location of the action
 * @property {[piece]} pieces e.g an array of all pieces on the board at the end (after all movement)

/**
 * @typedef {object} piece
 * @property {string} player - "playerOne", "playerTwo", or "Empty"
 * @property {string} direction - "up", "upRight", "upLeft", "down", "downRight", "downLeft", or "Empty"
 * @property {coords} coords - {s, r, q} i.e. an object with all three game coordinate axes
 */

/**
 * @typedef {object} coords
 * @property {string} s - The s axis (downLeft through upRight )
 * @property {string} q - The q axis (vertical)
 * @property {string} r - The r axis ( upLeft through downRight )
 * ```
 *    +s   +r
 * -q   Hex   +q
 *    -r   +s
 * ```
 * * s axis (upLeft through downRight, increments up left, decrements down right)
 * * q axis (horizontal axis, 0 is the left most, increments to the right)
 * * r axis (downLeft through upRight, decrements up right, increments down left)
 */

// player means the UI reference
const isPlayerValid = (player) => {
  if (PLAYERS.includes(player)) {
    return true;
  } else {
    return false;
  }
};

const isDirectionValid = (direction) => {
  if (DIRECTIONS.includes(direction)) {
    return true;
  } else {
    return false;
  }
};

const isJoinTypeValid = (joinType) => {
  if (MATCH_JOIN_TYPES.includes(joinType)) {
    return true;
  } else {
    return false;
  }
};

const isAudienceParticipationValid = (audienceParticipation) => {
  if (AUDIENCE_PARTICIPATION.includes(audienceParticipation)) {
    return true;
  } else {
    return false;
  }
};

const isVisibilityValid = (visibility) => {
  if (VISIBILITY.includes(visibility)) {
    return true;
  } else {
    return false;
  }
};

const isModeValid = (mode) => {
  if (MODE.includes(mode)) {
    return true;
  } else {
    return false;
  }
};

const isMatchStateValid = (state) => {
  if (MATCH_STATES.includes(state)) {
    return true;
  } else {
    return false;
  }
};

const isActionValid = (action) => {
  if (ACTIONS.includes(action)) {
    return true;
  } else {
    return false;
  }
};

const isCoordsValid = (coords) => {
  if (coords === null) {
    const message = "ERROR: The coords is null.";
    return { status: 400, message };
  }
  if (typeof coords != "object") {
    const message = "ERROR: The coords is not an object.";
    return { status: 400, message };
  }
  if (Array.isArray(coords)) {
    const message = "ERROR: The coords is an array.";
    return { status: 400, message };
  }
  const keys = Object.keys(coords);
  if (keys.length != 3) {
    const message =
      "ERROR: The coords must have exactly three keys, but the coords received has: " +
      keys.length;
    return { status: 400, message };
  }
  if (!keys.includes("s")) {
    const message = "ERROR: The coords does not include the s axis";
    return { status: 400, message };
  }
  if (typeof coords.s != "number") {
    const message = "ERROR: The coords s axis must be a number: " + coords.s;
    return { status: 400, message };
  }
  if (!keys.includes("q")) {
    const message = "ERROR: The coords does not include the q axis";
    return { status: 400, message };
  }
  if (typeof coords.q != "number") {
    const message = "ERROR: The coords q axis must be a number: " + coords.q;
    return { status: 400, message };
  }
  if (!keys.includes("r")) {
    const message = "ERROR: The coords does not include the r axis";
    return { status: 400, message };
  }
  if (typeof coords.r != "number") {
    const message = "ERROR: The coords r axis must be a number: " + coords.r;
    return { status: 400, message };
  }
  return { status: 200, message: "" };
};

const isJoinCodeValid = (joinCode) => {
  if (typeof joinCode !== "string") {
    return { status: 400, message: "Invalid input type." };
  }
  const cleanCode = joinCode.trim();
  if (cleanCode.length > 50) {
    return { status: 400, message: "Join code is too long." };
  }
  if (cleanCode.includes("<") || cleanCode.includes(">")) {
    return { status: 400, message: "Join Code has invalid characters." };
  }
  if (!cleanCode.includes("-")) {
    return { status: 400, message: "Join Code format is not valid." };
  }

  const [type, id] = cleanCode.split("-");

  if (!MATCH_JOIN_TYPES.includes(type)) {
    return { status: 400, message: "Join Code type is not valid." };
  }
  const idPattern = /^[A-NP-Z1-9]{7}$/;
  if (!idPattern.test(id)) {
    return { status: 400, message: "Join Code ID format is incorrect." };
  }
  const digits = id.match(/[1-9]/g) || [];
  if (digits.length !== 1) {
    return {
      status: 400,
      message: "Join Code ID must contain exactly one number.",
    };
  }

  return { status: 200, message: "" };
};

const isPieceValid = (piece) => {
  if (piece === null) {
    const message = "ERROR: The piece is null.";
    return { status: 400, message };
  }
  if (typeof piece != "object") {
    const message = "ERROR: The piece is not an object.";
    return { status: 400, message };
  }
  if (Array.isArray(piece)) {
    const message = "ERROR: The piece is an array.";
    return { status: 400, message };
  }
  const keys = Object.keys(piece);
  if (keys.length != 3) {
    const message =
      "ERROR: The piece must have exactly three keys, but the piece received has: " +
      keys.length;
    return { status: 400, message };
  }
  if (!keys.includes("player")) {
    const message = "ERROR: The piece does not include a player";
    return { status: 400, message };
  }
  if (piece.player != "Empty") {
    if (!isPlayerValid(piece.player)) {
      const message = "ERROR: The player is not valid";
      return { status: 400, message };
    }
  }
  if (!keys.includes("direction")) {
    const message = "ERROR: The piece does not include a direction";
    return { status: 400, message };
  }
  if (piece.direction != "Empty") {
    if (!isDirectionValid(piece.direction)) {
      const message = "ERROR: The direction is not valid";
      return { status: 400, message };
    }
  }
  if (!keys.includes("coords")) {
    const message = "ERROR: The piece does not include coords";
    return { status: 400, message };
  }
  if (isCoordsValid(piece.coords).status != 200) {
    const message = "ERROR: The coords are not valid";
    return { status: 400, message };
  }
  return { status: 200, message: "" };
};

const isMatchIDValid = (matchID) => {
  if (typeof matchID !== "string") {
    return false;
  }

  /*  The "Whitelist" Regex:
     ^      = Start of string
     [A-NP-Z1-9] = Allow A-N, P-Z (skips O) and 1-9 (skips 0). 
                   Note: This assumes uppercase. Add a-np-z if lowercase is okay.
     {7}    = Exactly 7 characters
     $      = End of string
  */
  const validPattern = /^[A-NP-Z1-9]{7}$/;

  if (!validPattern.test(matchID)) {
    return false;
  }

  if (utilsApp.textGotForbiddenChars(matchID)) {
    return false;
  }

  return true;
};

/**
 * @brief Validates a match object, handling both complete and partial match objects
 * @param {Object} match - The match object to validate
 * @param {boolean} isPartial - Whether this is a partial match (from GSI) or complete match
 */
const isMatchValid = (match, isPartial = false) => {
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: Starting validation for match " + match.matchID + " (partial: " + isPartial + ")");
  }
  
  // Basic object validation
  if (!(typeof match === "object")) {
    const message = "ERROR: The match is not an object.";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (match === null) {
    const message = "ERROR: The match is null.";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (Array.isArray(match)) {
    const message = "ERROR: The match is an array.";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  
  const keys = Object.keys(match);
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: Match has " + keys.length + " keys: " + JSON.stringify(keys));
  }
  
  // Always require matchID
  if (!keys.includes("matchID")) {
    const message = "ERROR: The match does not have a matchID";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  
  // For partial matches (from GSI), we only validate what's available
  if (isPartial) {
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Partial match validation passed for " + match.matchID);
    }
    return { status: 200, message: "Match is valid (partial)" };
  }
  
  // Full validation for complete matches
  if (!keys.includes("state")) {
    const message = "ERROR: The match does not include a state";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (!isMatchStateValid(match.state)) {
    const message = "ERROR: The match state is not valid: " + match.state;
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: state validated: " + match.state);
  }
  // Name checks below are shape checks ("namePart (USERID)" or "waiting"),
  // not the rules for entering a new name, so matches with older names load.
  if (keys.includes("winnerName")) {
    // there doesn't have to be a winnerName, but if there is
    if (match.winnerName != "") {
      // and it's not empty
      if (
        !(
          identityService.isFullNameShapeValid(match.winnerName) ||
          match.winnerName === "none"
        )
      ) {
        // the it's got to be a valid name format or "none"
        const message =
          "ERROR: The winnerName is not a valid: " + match.winnerName;
        return { status: 400, message };
      }
    }
  }
  if (!keys.includes("ownerName")) {
    const message = "ERROR: The match does not have an ownerName";
    return { status: 400, message };
  }
  if (!identityService.isFullNameShapeValid(match.ownerName)) {
    const message = "ERROR: The ownerName is not valid: " + match.ownerName;
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: ownerName validated: " + match.ownerName);
  }
  if (!keys.includes("ownerUserID")) {
    const message = "ERROR: The match does not have an ownerUserID";
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: ownerUserID validated: " + match.ownerUserID);
  }
  if (!identityService.isUserIDValid(match.ownerUserID)) {
    const message =
      "ERROR: The value in ownerUserID is not a valid userID: " +
      match.ownerUserID;
    return { status: 400, message };
  }
  if (!keys.includes("turn")) {
    const message = "ERROR: The match does not have an turn";
    return { status: 400, message };
  }
  if (!identityService.isUserIDValid(match.turn)) {
    const message =
      "ERROR: The value in turn is not a valid userID: " + match.turn;
    if (TROUBLESHOOTING) {
      console.log("isMatchValid: Failed - " + message);
    }
    return { status: 400, message };
  }
  if (TROUBLESHOOTING) {
    console.log("isMatchValid: turn validated: " + match.turn);
  }
  if (keys.includes("playerCodeP1")) {
    // playerCodeP1 only exists if the owner creates it
    // but if it does exist, it needs to be valid
    if (!identityService.isUserIDValid(match.playerCodeP1)) {
      const message =
        "ERROR: The playerCodeP1 has a value, but it is not a valid: " +
        match.playerCodeP1;
      return { status: 400, message };
    }
  }
  if (keys.includes("playerCodeP2")) {
    // playerCodeP2 only exists if the owner creates it
    // but if it does exist, it needs to be valid
    if (!identityService.isUserIDValid(match.playerCodeP2)) {
      const message =
        "ERROR: The playerCodeP2 has a value, but it is not a valid: " +
        match.playerCodeP2;
      return { status: 400, message };
    }
  }
  if (keys.includes("watcherCode")) {
    // watcherCode only exists if the owner creates it
    // but if it does exist, it needs to be valid
    if (!identityService.isUserIDValid(match.watcherCode)) {
      const message =
        "ERROR: The watcherCode has a value, but it is not a valid: " +
        match.watcherCode;
      return { status: 400, message };
    }
  }
  if (!keys.includes("p1Score")) {
    const message = "ERROR: The match does not have p1Score";
    return { status: 400, message };
  }
  if (typeof match.p1Score != "number") {
    const message = "ERROR: The p1Score is not a number: " + match.p1Score;
    return { status: 400, message };
  }
  if (!keys.includes("p1Name")) {
    const message = "ERROR: The match does not have a p1Name";
    return { status: 400, message };
  }
  if (!identityService.isFullNameShapeValid(match.p1Name)) {
    const message = "ERROR: The p1Name is not a valid: " + match.p1Name;
    return { status: 400, message };
  }
  if (!keys.includes("p1UserID")) {
    const message = "ERROR: The match does not have an p1UserID";
    return { status: 400, message };
  }
  if (!identityService.isUserIDValid(match.p1UserID)) {
    const message =
      "ERROR: The value in p1UserID is not a valid userID: " + match.p1UserID;
    return { status: 400, message };
  }
  if (!keys.includes("p2Name")) {
    const message = "ERROR: The match does not have a p2Name";
    return { status: 400, message };
  }
  if (!identityService.isFullNameShapeValid(match.p2Name)) {
    const message = "ERROR: The p2Name is not a valid: " + match.p2Name;
    return { status: 400, message };
  }
  if (!keys.includes("p2UserID")) {
    const message = "ERROR: The match does not have an p2UserID";
    return { status: 400, message };
  }
  if (!identityService.isUserIDValid(match.p2UserID)) {
    const message =
      "ERROR: The value in p2UserID is not a valid userID: " + match.p2UserID;
    return { status: 400, message };
  }
  if (!keys.includes("p2Score")) {
    const message = "ERROR: The match does not have p2Score";
    return { status: 400, message };
  }
  if (typeof match.p2Score != "number") {
    const message = "ERROR: The p2Score is not a number: " + match.p1Score;
    return { status: 400, message };
  }
  // gotWatchers is optional (added after matches were already created)
  if (keys.includes("gotWatchers")) {
    if (!["Y", "N"].includes(match.gotWatchers)) {
      const message =
        "ERROR: The value in gotWatchers is not correct: " + match.gotWatchers;
      return { status: 400, message };
    }
  }
  if (keys.includes("watchers")) {
    // Does not exist when there are no watchers
    // but if it does exist, got to be an array
    if (!Array.isArray(match.watchers)) {
      const message = "ERROR: match.watchers is not an array.";
      return { status: 400, message };
    }
  }
  if (!keys.includes("visibility")) {
    const message = "ERROR: The match does not have a visibility attribute";
    return { status: 400, message };
  }
  if (!isVisibilityValid(match.visibility)) {
    const message =
      "ERROR: The value for 'visibility' is not valid: " + match.visibility;
    return { status: 400, message };
  }
  // the created key is required and has to be an object, and it has to be populated correctly
  if (!keys.includes("created")) {
    const message = "ERROR: The match does not have key called 'created'";
    return { status: 400, message };
  }
  if (typeof match.created != "object") {
    const message = "ERROR: The created timeStamps are not an object";
    return { status: 400, message };
  }
  const createdResult = dateTimeUtils.areTimeStampsValid(match.created);
  if (createdResult.status != 200) {
    const message =
      "ERROR: The created timeStamps are not valid: " + createdResult.message;
    return { status: 400, message };
  }
  // the started key is required and has to be an object, but it's okay if it's empty
  if (!keys.includes("started")) {
    const message = "ERROR: The match does not have key called 'started'";
    return { status: 400, message };
  }
  if (typeof match.started != "object") {
    const message = "ERROR: The started timeStamps are not an object";
    return { status: 400, message };
  }
  if (Object.keys(match.started).length > 0) {
    //but if it is not empty, then it's got to be valid
    const startedResult = dateTimeUtils.areTimeStampsValid(match.started);
    if (startedResult.status != 200) {
      const message =
        "ERROR: The started timeStamps are not valid: " + startedResult.message;
      return { status: 400, message };
    }
  }
  // the ended key is required and has to be an object, but it's okay if it's empty
  if (!keys.includes("ended")) {
    const message = "ERROR: The match does not have key called 'ended'";
    return { status: 400, message };
  }
  if (typeof match.ended != "object") {
    const message = "ERROR: The ended timeStamps are not an object";
    return { status: 400, message };
  }
  if (Object.keys(match.ended).length > 0) {
    //but if it is not empty, then it's got to be valid
    const endedResult = dateTimeUtils.areTimeStampsValid(match.ended);
    if (endedResult.status != 200) {
      const message =
        "ERROR: The ended timeStamps are not valid: " + endedResult.message;
      return { status: 400, message };
    }
  }
  if (!keys.includes("lastMove")) {
    const message = "ERROR: The lastMove key is missing";
    return { status: 400, message };
  }
  if (!dateTimeUtils.isEpochValid(match.lastMove)) {
    const message =
      "ERROR: lastMove is not a valid epoch time: " + match.lastMove;
    return { status: 400, message };
  }
  if (!keys.includes("moves")) {
    const message = "ERROR: The moves key is missing";
    return { status: 400, message };
  }
  if (!Array.isArray(match.moves)) {
    const message = "ERROR: match.moves is not an array.";
    return { status: 400, message };
  }
  // TTL validation: TTL is only required for pending matches
  // Started/finished matches don't have TTL as it gets removed by the backend
  if (match.state === "pending") {
    if (!keys.includes("ttl")) {
      const message = "ERROR: The ttl key is missing";
      return { status: 400, message };
    }
    if (!dateTimeUtils.isEpochValid(match.ttl)) {
      const message = "ERROR: ttl is not a valid epoch time: " + match.ttl;
      return { status: 400, message };
    }
  } else {
    // For non-pending matches, TTL should not be present or should be ignored
    if (TROUBLESHOOTING && keys.includes("ttl")) {
      console.log("isMatchValid: TTL present on non-pending match (this is unusual but not invalid)");
    }
  }
  if (!keys.includes("pieces")) {
    const message = "ERROR: The pieces array is missing";
    return { status: 400, message };
  }
  if (!Array.isArray(match.pieces)) {
    const message = "ERROR: match.pieces is not an array.";
    return { status: 400, message };
  }
  if (!keys.includes("audienceParticipation")) {
    const message = "ERROR: The audienceParticipation key is missing";
    return { status: 400, message };
  }
  if (!isAudienceParticipationValid(match.audienceParticipation)) {
    const message =
      "ERROR: The value for audience pariticpation is not valid: " +
      match.audienceParticipation;
    return { status: 400, message };
  }
  if (!keys.includes("mode")) {
    const message = "ERROR: The mode key is missing";
    return { status: 400, message };
  }
  if (!isModeValid(match.mode)) {
    const message =
      "ERROR: The value for mode pariticpation is not valid: " + match.mode;
    return { status: 400, message };
  }

  return { status: 200, message: "" };
};

/**
 * adds a piece to the current match and then stores the entire move.
 * @param {coords} coords  {s, r, q} i.e. an object with all three game coordinate axes
 * @return {{status: number}} 200 or 400
 */
const addPiece = async ({ coords, player }) => {
  // console.log(
  //   "-----addPiece: " +
  //     JSON.stringify(coords) +
  //     " " +
  //     JSON.stringify(player) +
  //     " --------"
  // );
  // console.log("The timestamp is: " + new Date().toISOString());
  // console.trace();

  const piece2Add = {
    player: player,
    direction: getDirection(matchService.getTurn().player),
    coords: coords,
  };

  //////
  const currentMatchIDResult = getCurrentMatchID();
  if (currentMatchIDResult.status != 200) {
    throw new MatchServiceError(
      `There is no current Match`,
      currentMatchIDResult.status,
    );
  }
  let currentMatchIndex = 0;
  let foundIt = 0;
  for (const match of matchState.cache) {
    if (match.matchID === currentMatchIDResult.currentMatchID) {
      foundIt = currentMatchIndex;
    }
    currentMatchIndex++;
  }
  matchState.cache[foundIt].pieces.push(piece2Add);

  return { status: 200 };
};

/**
 * removes a piece from the current match
 * @param {coords} coords  {s, r, q} i.e. an object with all three game coordinate axes
 * @return {{status: number}} 200 or 400
 */
const removePiece = async (coords) => {
  const matchResponse = await matchService.getCurrentMatch();
  if (matchResponse.status != 200) {
    return { status: matchResponse.status };
  }
  const currentMatch = matchResponse.currentMatch;
  const player = getTurn().player;

  if (player === "Empty") {
    return { status: 400 };
  }

  let index = 0;
  let foundIt = false;
  for (const piece of currentMatch.pieces) {
    if (
      piece.coords.s === coords.s &&
      piece.coords.q === coords.q &&
      piece.coords.r === coords.r
    ) {
      foundIt = true;
      break;
    }
    index++;
  }

  if (foundIt) {
    currentMatch.pieces.splice(index, 1); //mutates original array in place
  }
};

/**
 * @returns {{status:number, pieces:[piece]}} Return object has these keys:
 * * status is 200 or 400
 * * error is an error message if it's not 200
 * * pieces is an array. It is a clone of the real time array of pieces in the current match
 * * Can't use this to update a piece in the array. Need to call updatePiece({index,piece}) for that.
 */
const getPieces = () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    const message = "ERROR: The there is no current match";
    console.log(message);
    return { status: 400, error: message };
  }
  const currentMatch = result.currentMatch;
  if (!Array.isArray(currentMatch.pieces)) {
    // e.g. only a partial copy of the match is loaded so far
    const message = "ERROR: The current match has no pieces loaded";
    console.log(message);
    return { status: 400, error: message };
  }
  // Returns a clone not a pointer. Can't use this to update pieces
  // need to call updatePiece for that
  const pieces = JSON.parse(JSON.stringify(currentMatch.pieces));
  return { status: 200, pieces };
};

/**
 * Updates one piece in the current matches array of pieces
 * @param {{index: number, piece: piece}}
 * @return {{status: number, error: string}}
 * * status can be 200 or 400
 * * if status is 400, the error will contain the reason
 */
const updatePiece = async ({ index, piece }) => {
  if (typeof index != "number") {
    const message = "ERROR: The index input has to be a number";
    console.log(message);
    return { status: 400, error: message };
  }
  if (index < 0) {
    const message = "ERROR: The index input has to be greater than zero";
    console.log(message);
    return { status: 400, error: message };
  }

  //////
  const currentMatchIDResult = getCurrentMatchID();
  if (currentMatchIDResult.status != 200) {
    throw new MatchServiceError(
      `There is no current Match`,
      currentMatchIDResult.status,
    );
  }
  let currentMatchIndex = 0;
  let foundIt = 0;
  for (const match of matchState.cache) {
    if (match.matchID === currentMatchIDResult.currentMatchID) {
      foundIt = currentMatchIndex;
    }
    currentMatchIndex++;
  }
  matchState.cache[foundIt].pieces[index] = piece;

  return { status: 200 };
};

/**
 * Retrieves active matches where playingAs identity is a player
 * and sets the most recent lastMove to be the current match and renders that.
 * If the identity has no matches,
 * then it renders the board and scoreboard as empty and opens the start menu
 * @returns {string} matchID
 */
const loadPlayingAsMatches = async ({ createMatch }) => {
  if (TROUBLESHOOTING) {
    console.log("loadPlayingAsMatches: Starting with createMatch =", createMatch);
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    if (TROUBLESHOOTING) {
      console.log("loadPlayingAsMatches: Session expired");
    }
    return;
  }
  const pAIdentity = pAIdentityResponse.identity;

  const pAUserID = pAIdentity.userID;
  if (TROUBLESHOOTING) {
    console.log("loadPlayingAsMatches: Loading matches for userID:", pAUserID);
  }
  const params = {
    playerFilter: true, //default
    watcherFilter: false,
    ownerFilter: false,
    activeFilter: true, //default
    finishedFilter: false,
  };
  const loadResult = await loadMatches(params);
  const matches = loadResult.matches; // e.g. not finsihed or draw
  
  if (TROUBLESHOOTING) {
    console.log("loadPlayingAsMatches: loadMatches returned " + matches.length + " matches");
    console.log("loadPlayingAsMatches: Cache now has " + matchState.cache.length + " matches");
  }

  switch (true) {
    case matches.length === 0: //ain't never played a game
      if (TROUBLESHOOTING) {
        console.log("loadPlayingAsMatches: No matches found");
      }
      // don't create one automatically. this is the normal case.
      if (createMatch === false) {
        await setCurrentMatchID("noCurrentMatch");
        await scoreBoard.render();
        await board.render();
      } else {
        if (TROUBLESHOOTING) {
          console.log("loadPlayingAsMatches: Creating new match against computer");
        }
        await matchService.create({
          ownerName: pAIdentity.name, // has extra permissions, like cancelling the match (if not started yet, etc.)
          p1Name: pAIdentity.name, // person who goes first rendered on the left
          p2Name: COMPUTER_NAME, // person who goes second, rendered on the right
          visibility: "invited",
          audienceParticipation: "disabled",
          mode: "sameDevice",
        }); //create a new match and just assume playing as is going first
        zoom.resizeCanvas(); // Gotta do this next
        utilsApp.makeNavsSmall();
        scoreBoard.render();
        board.render(); // and we are off
      }

      break;
    case matches.length > 0:
      if (TROUBLESHOOTING) {
        console.log("loadPlayingAsMatches: Found " + matches.length + " matches, loading most recent");
      }
      let match2Load = router.getURLMatchID();
      const currentMatchIDBefore = matchState.currentMatchID;

      if (match2Load === "") {
        if (matches.some((m) => m.matchID === currentMatchIDBefore)) {
          // Stay on the match you were looking at (e.g. after renaming
          // yourself), if it's still one of this identity's matches
          match2Load = currentMatchIDBefore;
          if (TROUBLESHOOTING) {
            console.log("loadPlayingAsMatches: Staying on current match:", match2Load);
          }
        } else {
          // matches will come in order of last move
          match2Load = matches[0].matchID;
          if (TROUBLESHOOTING) {
            console.log("loadPlayingAsMatches: No URL match specified, using most recent:", match2Load);
          }
        }
      } else {
        if (TROUBLESHOOTING) {
          console.log("loadPlayingAsMatches: Loading match from URL:", match2Load);
        }
        router.clearURLMatchID();
      }

      if (match2Load === currentMatchIDBefore) {
        // setCurrentMatchID returns early for the same ID, so refresh
        // explicitly to pick up anything that changed (e.g. the new name)
        await refreshCurrentMatch();
      } else {
        await setCurrentMatchID(match2Load);
      }
      const currentMatch = matchService.getCurrentMatch().currentMatch;
      zoom.resizeCanvas(); // Gotta do this first
      utilsApp.makeNavsSmall();
      scoreBoard.render();
      board.render(); // and we are off
      
      // Initialize chat for this match
      try {
        const { chat } = await import("../chat/chat_v2.js");
        await chat.show(currentMatch);
      } catch (error) {
        console.warn("Chat system failed to load:", error);
      }

      // what if we just loaded an active match, but it's not your turn?
      if (currentMatch.active === "active") {
        await board.calculateEndGame(); // make sure there's not a winner
        if (currentMatch.turn === COMPUTER_USERID) {
          board.handleComputerTurn(); // can't think of how this would happen, but I guess I'll leave it
        }
      }
  }
};

/**
 * Loads all matches that fit the filter
 * @param {{
    playerFilter: boolean, 
    watcherFilter: boolean,
    ownerFilter: boolean,
    activeFilter: boolean, 
    finishedFilter: boolean,
  }} 
 * @returns {[matches]} and array of matches where the pAIdentity was involved as
 * a player, watcher or owner. 
 */
const loadMatches = async (filters) => {
  let response;
  let pAUserID;
  let matchesReturned = [];
  if (TROUBLESHOOTING) {
    console.log("loadMatches: Starting with filters:", filters);
  }
  try {
    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    pAUserID = pAIdentity.userID;

    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "loadMatches",
      pAUserID: pAUserID,
      filters: filters,
    };

    if (TROUBLESHOOTING) {
      console.log("loadMatches: Making request to backend with body:", body);
    }

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    if (TROUBLESHOOTING) {
      console.log("loadMatches: Backend returned " + (bodyAsJSON.matches ? bodyAsJSON.matches.length : 0) + " matches");
    }

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    matchesReturned = bodyAsJSON.matches;
    let matchesAddedToCache = 0;
    for (const match of matchesReturned) {
      if (TROUBLESHOOTING) {
        console.log("loadMatches: Processing match " + match.matchID + " with state " + match.state);
      }
      const cacheCountBefore = matchState.cache.length;
      updateMatchInCache(match); //updates or adds
      const cacheCountAfter = matchState.cache.length;
      if (cacheCountAfter > cacheCountBefore || matchState.cache.find(m => m.matchID === match.matchID)) {
        matchesAddedToCache++;
        if (TROUBLESHOOTING) {
          console.log("loadMatches: Match " + match.matchID + " successfully added to cache");
        }
      } else {
        if (TROUBLESHOOTING) {
          console.log("loadMatches: WARNING - Match " + match.matchID + " was NOT added to cache (validation likely failed)");
        }
      }
    }
    if (TROUBLESHOOTING) {
      console.log("loadMatches: Added " + matchesAddedToCache + " out of " + matchesReturned.length + " matches to cache");
      console.log("loadMatches: Total cache size is now: " + matchState.cache.length);
    }
  } catch (error) {
    if (TROUBLESHOOTING) {
      console.log("loadMatches: Error occurred:", error);
    }
    console.error(
      `Error in setMatchState or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, matches: [], error: error.message };
  }

  return { status: 200, matches: matchesReturned };
};

/**
 * Sorts the match cache by lastMove descending (newest first)
 * @param {Array} matches - The matchState.cache array
 * @returns {Array} - A new sorted array
 */
const sortMatchesByLastMove = (matches) => {
  // We use the spread operator [...] to create a shallow copy
  // so we don't mutate the original cache directly.
  const sorted = [...matches].sort((matchA, matchB) => {
    const timeA = matchA.lastMove;
    const timeB = matchB.lastMove;

    // Descending order: Newest (largest number) first
    return timeB - timeA;
  });

  return sorted;
};

/**
 * Tells you if a userID is watching a match. Always returns true or false
 * even if no one is watching a match and therefore the wathers array doesn't exist
 * @param {{userID:string,match:object}} input
 * @returns boolean
 */
const isWatching = ({ userID, match }) => {
  //'watchers' exists and is an array before calling .includes()
  const watchers = match?.watchers;

  // Ensure watchers is actually an array to avoid errors
  if (Array.isArray(watchers)) {
    return watchers.includes(userID);
  }
  return false;
};

/**
 * Sets the current match to the ID that is provided.
 * The current match is the one that' being rendered by gameBoard module
 * The match has to already be loaded into the cache
 * @param {string} matchID
 * @returns {{status: number, message: string}}
 */
const setCurrentMatchID = async (matchID) => {
  if (matchID === "noCurrentMatch") {
    // what if the identity has never played a match?
    // That's okay, and we set it to noCurrentMatch
    matchState.currentMatchID = matchID;

    return { status: 200, message: "" };
  }
  if (!isMatchIDValid(matchID)) {
    return {
      status: 400,
      message: "The current matchID is not valid: " + matchID,
    };
  }
  if (matchState.currentMatchID === matchID) {
    return {
      status: 400,
      message: "The current match is already set to " + matchID,
    };
  }
  for (const match of matchState.cache) {
    if (match.matchID === matchID) {
      // has to be in the cache
      matchState.currentMatchID = matchID; // that's really all it is...
      await refreshCurrentMatch(); // pull from DB incase something has changed we don't know about
      loadNotPlayingAsIdentity(match);
      if (
        match.lastMove < dateTimeUtils.epochTimeMinus31Days() &&
        match.active === "active"
      ) {
        matchService.setMatchState("abandoned");
      }
      return { status: 200, message: "" };
    }
  }
};

const loadNotPlayingAsIdentity = async (match) => {
  // load the not playing as identity into the identity cache
  // because if it's missing, this is actually an abandoned match
  // also a good idea to pre-stage their rank and stuff
  // and get identity also subscribed
  if (
    match.p1UserID === "waiting" ||
    match.p1UserID === COMPUTER_USERID ||
    match.p2UserID === "waiting" ||
    match.p2UserID === COMPUTER_USERID
  ) {
    // the not playing as identity should not be loaded in this case
    return;
  }

  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return;
  }
  const pAuserID = pAIdentityResponse.identity.userID;
  let result = null;
  if (
    pAuserID === match.p1UserID &&
    match.p2UserID &&  // ← Add null check
    identityService.isInCache(match.p2UserID) === false
  ) {
    // Debug logging for matchService p2 identity lookup  
    console.log("matchService_v2 p2 lookup: match.p2UserID =", match.p2UserID, "type =", typeof match.p2UserID);
    console.log("matchService_v2 p2 lookup: call stack:", new Error().stack);
    
    result = identityService.getIdentity(match.p2UserID);
  }
  if (
    pAuserID === match.p2UserID &&
    match.p1UserID &&  // ← Add null check
    identityService.isInCache(match.p1UserID) === false  // ← Add === false
  ) {
    // Debug logging for matchService p1 identity lookup  
    console.log("matchService_v2 p1 lookup: match.p1UserID =", match.p1UserID, "type =", typeof match.p1UserID);
    console.log("matchService_v2 p1 lookup: call stack:", new Error().stack);
    
    result = identityService.getIdentity(match.p1UserID);
  }
  // I guess I don't really need the result, but...
};

/**
 * Creates a new match and sets it as the currentMatch
 * @param {{ownerName: name, p1Name: name, p2Name:panme, visibility: string, mode: string}} object The input keys keys are as follows
 * * ownerName is required. Must be a valid name
 * * p1Name is required. It could be a valid name or it could be "waiting"
 * * p2Name is required. It could be a valid name or it could be "waiting"
 * * visibility is required. could be "public" or "private".
 * * mode is required. Could be "sameDevice" or "ownDevice"
 */

const create = async (matchData) => {
  // creates a match and loads it into active matches, and sets it as the current match
  // you can only create a match when you know who both players are
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;

  let response;
  try {
    const resource = "/v1/match";
    const body = JSON.stringify({
      idempotencyKey: idem.generateKey("match"),
      action: "createMatch",
      matchData,
      pAUserID: pAIdentity.userID,
    });
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
      body: body,
    });
    if (!response.ok) {
      throw new MatchServiceError(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.log(error.message);
    return { status: 500, message: error.message, match: {} };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });
  //matchState.cache.push(bodyAsJSON.match); //it will come back in the subscription
  await setCurrentMatchID(bodyAsJSON.match.matchID);
  return { status: 200 };
};

/**
 * If there is a current match, returns the entire match object.
 * @returns {{status: number, currentMatch:object}} The keys in the return object are
 * * status can be 200 or 404 if there is no current Match
 * * currentMatch is the full matchObject
 */
const getCurrentMatch = () => {
  for (const match of matchState.cache) {
    if (match.matchID === getCurrentMatchID().currentMatchID) {
      return { status: 200, currentMatch: match };
    }
  }
  return { status: 404, currentMatch: {} };
};

/**
 *
 * @returns {{status: number, currentMatchID: string, error: string}}
 * * status can be 200 if there is a currentMatch and 404 if there is not
 * * currentMatchID is the ID of the current Match or empty
 * * error is the error message, if presnt
 */
const getCurrentMatchID = () => {
  if (
    matchState.currentMatchID === "notSet" ||
    matchState.currentMatchID === "noCurrentMatch"
  ) {
    return {
      status: 404,
      error: "There is no current match ID",
      currentMatchID: "",
    };
  }
  return { status: 200, currentMatchID: matchState.currentMatchID };
};

const setMatchState = async (state) => {
  // console.log("-----setMatchState:" + state + "--------");
  // console.log("The Intial timestamp is: " + new Date().toISOString());

  let response;
  let currentMatch;
  let pAUserID;
  try {
    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    pAUserID = pAIdentity.userID;

    if (!isMatchStateValid(state))
      throw new MatchServiceError(`Invalid state: ${state}`, 400);

    const result = getCurrentMatch();
    if (result.status != 200)
      throw new MatchServiceError("There is no current match", 404);
    currentMatch = result.currentMatch;
    // safety check. Should not happen, but...
    if (currentMatch.state === state) {
      console.log(
        `setMatchState: Match is already in state "${state}". Skipping.`,
      );
      return { status: 200 };
    }

    const resource = "/v1/match";
    let body = {
      idempotencyKey: idem.generateKey("match"),
      action: "setMatchState",
      pAUserID: pAUserID,
      state: state,
      matchID: currentMatch.matchID,
    };

    if (state === "notifyResign") {
      body.pResigned = currentMatch.turn;
    }

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    const updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in setMatchState or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, error: error.message };
  }
  return { status: 200 };
};

/**
 * Returns whose turn it is in this format
 * @returns {{status: number, error: string, player: "playerOne or playerTwo", userID: string}}
 * * status is 200 or 404 (if there is no current match)
 * * error is a message that's available only if the status is 404
 * * player is "playerOne" or "playerTwo"
 * * userID is the userID of whose turn it is
 */
const getTurn = () => {
  const result = getCurrentMatch();
  // Was `result.status = 200` (an assignment, always true), so with no current
  // match it reported "playerOne" with an undefined userID.
  if (result.status === 200) {
    return {
      status: 200,
      error: "",
      player:
        result.currentMatch.turn === result.currentMatch.p1UserID
          ? "playerOne"
          : "playerTwo",
      userID: result.currentMatch.turn,
    };
  } else {
    return {
      status: 404,
      error: "There is no current match",
      player: "",
      userID: "",
    };
  }
};

const isPATurn = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return;
  }
  const pAUserID = pAIdentityResponse.identity.userID;
  const currentMatchResult = getCurrentMatch();
  if (currentMatchResult.status != 200) {
    // no current match
    return;
  }
  const turn = currentMatchResult.currentMatch.turn;
  if (turn === pAUserID) {
    return true;
  } else {
    return false;
  }
};

/**
 * Sets the turn of the current match to be the userID of whose turn is it.
 * Input is "playerOne" or "playerTwo", which is the front end generic reference that doesn't care
 * what identities happen to be in the match.
 * @param {string} player
 * @returns
 */
const setTurn = async ({ player, coords, actionB4 }) => {
  if (!matchService.isPlayerValid(player)) {
    console.log(
      "Error: attempted to set the turn to an invalid player: " + player,
    );
    mouse.clearDownEventInProgress();
    return;
  }

  // save the turn
  let response;

  try {
    const result = getCurrentMatch();
    if (result.status != 200) {
      throw new MatchServiceError("Unable to find a current match", 404);
    }
    const currentMatch = result.currentMatch;
    let directionAction;
    if (currentMatch.turn === currentMatch.p1UserID) {
      directionAction = currentMatch.p1Direction;
    }
    if (currentMatch.turn === currentMatch.p2UserID) {
      directionAction = currentMatch.p2Direction;
    }

    // at the point, a move has just ended. We want to capture
    // the player from currentMatch BEFORE we change the turn
    const move = {
      moveEpoch: dateTimeUtils.epochTime(),
      player: currentMatch.turn,
      p1Score: currentMatch.p1Score,
      p2Score: currentMatch.p2Score,
      action: actionB4,
      coords: coords,
      direction: directionAction,
      pieces: currentMatch.pieces, // takes a crap ton of space
    };

    let newTurnValue = "initialValue";
    // case 1: What if we are setting the turn to playerOne, but they haven't joined yet?
    if (currentMatch.p1Name === "waiting" && player === "playerOne") {
      newTurnValue = "waiting";
    }
    // case 2: What if we are setting the turn to playerTwo, but they haven't joined yet?
    if (currentMatch.p2Name === "waiting" && player === "playerTwo") {
      newTurnValue = "waiting";
    }
    // case 3: we are setting the turn to play who has in fact joined the game
    if (newTurnValue != "waiting") {
      newTurnValue =
        player === "playerOne" ? currentMatch.p1UserID : currentMatch.p2UserID;
    }

    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    const pAUserID = pAIdentity.userID;
    const removeAttribute =
      currentMatch.turn === currentMatch.p1UserID
        ? "p1RemovesUsed"
        : "p2RemovesUsed";
    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "setTurn",
      pAUserID: pAUserID,
      turn: newTurnValue,
      matchID: currentMatch.matchID,
      pieces: currentMatch.pieces,
      move: move,
      removeAttribute: removeAttribute,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    const updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in setTurn or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, error: error.message };
  } finally {
    // only now do we allow another mouse down event to come in
    mouse.clearDownEventInProgress();
  }

  return { status: 200 };
};

const changeTurn = async (coords) => {
  if (TROUBLESHOOTING) {
    console.log(
      "-----changeTurn: This player's turn just ended: " +
        matchService.getTurn().userID +
        "--------"
    );
    console.log("The timestamp is: " + new Date().toISOString());
  }
  //console.trace();

  const currentMatch = matchService.getCurrentMatch().currentMatch;
  await scoreBoard.updateScores(currentMatch.pieces);
  let setTurnResult;
  let actionB4;
  if (currentMatch.state != "finished") {
    switch (matchService.getTurn().player) {
      case "playerOne":
        actionB4 = getAction("playerOne");
        await setAction({ action: "push", player: "playerTwo" }); //default them to push
        if (TROUBLESHOOTING) {
          console.log("changeTurn: Switching from playerOne to playerTwo");
        }
        setTurnResult = await setTurn({
          player: "playerTwo",
          coords,
          actionB4,
        });
        break;
      case "playerTwo":
        actionB4 = getAction("playerTwo");
        await setAction({ action: "push", player: "playerOne" }); //default them to push
        if (TROUBLESHOOTING) {
          console.log("changeTurn: Switching from playerTwo to playerOne");
        }
        setTurnResult = await setTurn({
          player: "playerOne",
          coords,
          actionB4,
        });
    }
  }

  if (setTurnResult.status != 200) {
    if (TROUBLESHOOTING) {
      console.log("changeTurn: Error: unable to change the turn");
    }
    return setTurnResult;
  }

  if (TROUBLESHOOTING) {
    console.log("changeTurn: Turn change successful, checking for computer turn");
  }

  // now that it's the start of a new turn,
  // figure out if they have any moves. If not, it's game over
  await board.calculateEndGame();
  const currentMatchAfterTurn = matchService.getCurrentMatch().currentMatch;
  
  if (TROUBLESHOOTING) {
    console.log("changeTurn: Current turn after change: " + currentMatchAfterTurn.turn);
    console.log("changeTurn: Computer userID: " + COMPUTER_USERID);
    console.log("changeTurn: Match active status: " + currentMatchAfterTurn.active);
  }
  
  // and finally, this is where we jumpstart the computer move sequence:
  if (
    currentMatchAfterTurn.turn === COMPUTER_USERID &&
    currentMatchAfterTurn.active === "active"
  ) {
    if (TROUBLESHOOTING) {
      console.log("changeTurn: Starting computer turn");
    }
    board.handleComputerTurn();
  }
  return { status: 200 };
};

/**
 * Returns the score for a full name in the current match
 * @param {string} name
 * @returns {number} score
 * * returns 0 if there is no current match or any other error
 */
const getScore = (name) => {
  if (!identityService.isFullNameShapeValid(name)) {
    const message = "ERROR: The name is not valid: " + name;
    console.log(message);
    return 0;
  }
  const result = getCurrentMatch();
  if (result.status != 200) {
    const message = "ERROR: The there is no current match";
    console.log(message);
    return 0;
  }
  const currentMatch = result.currentMatch;

  // what if we are getting the score of someone who hasn't joined yet?
  if (name === "waiting") {
    return 0;
  }

  // can only make the comparison if it's not waiting, so...
  if (currentMatch.p1Name != "waiting") {
    if (
      currentMatch.p1UserID === "computer" ||
      currentMatch.p1UserID === identityService.parseUserID(name)
    ) {
      return currentMatch.p1Score;
    }
  }

  // can only make the comparison if it's not waiting, so...
  if (currentMatch.p2Name != "waiting") {
    if (
      currentMatch.p2UserID === "computer" ||
      currentMatch.p2UserID === identityService.parseUserID(name)
    ) {
      return currentMatch.p2Score;
    }
  }

  const message = "ERROR: The name is not in the current match: " + name;
  console.log(message);

  return 0;
};

/**
 * Sets the score for the userID in the current match
 * @parameter {{ userID: string, score: number }}
 * @returns {{status: number, error: string}}
 * * status can be 200 or 400
 * * error only exists if the status is 400
 */
const setScore = ({ userID, score }) => {
  if (!identityService.isUserIDValid(userID)) {
    const message = "ERROR: The userID is not valid: " + userID;
    console.log(message);
    return { status: 400, error: message };
  }
  const result = getCurrentMatch();
  if (result.status != 200) {
    const message = "ERROR: The there is no current match";
    console.log(message);
    return { status: 400, error: message };
  }
  const currentMatch = result.currentMatch;
  if (currentMatch.p1UserID === userID) {
    currentMatch.p1Score = score;
    return { status: 200 };
  }
  if (currentMatch.p2UserID === userID) {
    currentMatch.p2Score = score;
    return { status: 200 };
  }

  const message = "ERROR: The userID is not in the current match: " + userID;
  console.log(message);
  return { status: 400, error: message };
};

const join = async (code) => {
  const result = isJoinCodeValid(code);
  if (result.status != 200) {
    return result;
  }

  let response;
  let updatedMatch;

  try {
    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    const pAUserID = pAIdentity.userID;
    const pAName = pAIdentity.name;

    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "join",
      code: code,
      pAUserID: pAUserID,
      pAName: pAName,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      const bodyAsText = await response.text();
      const bodyAsJSON = JSON.parse(bodyAsText);
      throw new MatchServiceError(
        bodyAsJSON.message,
        response.status,
        bodyAsJSON?.errorType,
        bodyAsJSON?.matchToJoin,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    updatedMatch = bodyAsJSON.match;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
  } catch (error) {
    console.error(
      `Error joining the match ${error.status || 500}: ${error.message}`,
    );
    return {
      status: error.status || 500,
      error: error.message,
      errorType: error?.errorType,
      matchToJoin: error?.matchToJoin,
    };
  }

  return {
    status: 200,
    message: "You have joined the match",
    matchID: updatedMatch.matchID,
  };
};

/**
 * This goes through the in memory cache of matches and updates
 * and user names to the new name
 * @param {{userID: string, name:string}} input object has these keys:
 * * userID is just the ID
 * * name is the new namepart
 */
const updateUserName = ({ userID, name }) => {
  const newName = name + " (" + userID + ")";
  for (const match of matchState.cache) {
    const ownerUserID = match.ownerUserID;
    const p1UserID = match.p1UserID;
    const p2UserID = match.p2UserID;
    if (userID === ownerUserID) {
      match.ownerName = newName;
    }
    if (userID === p1UserID) {
      match.p1Name = newName;
    }
    if (userID === p2UserID) {
      match.p2Name = newName;
    }
  }
};

const generateAndStoreCode = async (codeType) => {
  let response;
  let updatedMatch;

  try {
    const result = getCurrentMatch();
    if (result.status != 200) {
      throw new MatchServiceError("Unable to find a current match", 404);
    }
    const currentMatch = result.currentMatch;

    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    const pAUserID = pAIdentity.userID;

    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "generateCode",
      codeType: codeType,
      pAUserID: pAUserID,
      matchID: currentMatch.matchID,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in setTurn or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, error: error.message };
  }

  switch (codeType) {
    case "playerCodeP1":
      return { status: 200, code: updatedMatch.playerCodeP1 };
      break;
    case "playerCodeP2":
      return { status: 200, code: updatedMatch.playerCodeP2 };
      break;
    case "watcherCode":
      return { status: 200, code: updatedMatch.watcherCode };
      break;
    default:
      return { status: 400, code: "" };
  }
};
/**
 * Generates a code that allows someone to join as player one
 * @returns playerCodeP1
 */
const generatePlayerCodeP1 = async () => {
  const playerCodeP1Result = await generateAndStoreCode("playerCodeP1");
  if (playerCodeP1Result.status != 200) {
    return "Error generating code";
  }
  return playerCodeP1Result.code;
};

/**
 * Generates a code that allows someone to join as player two
 * @returns playerCodeP2
 */
const generatePlayerCodeP2 = async () => {
  const playerCodeP2Result = await generateAndStoreCode("playerCodeP2");
  if (playerCodeP2Result.status != 200) {
    return "Error generating code";
  }
  return playerCodeP2Result.code;
};

/**
 * Generates a code that allows someone to join as a watcher
 * @returns watcherCode
 */
const generateWatcherCode = async () => {
  const watcherCodeResult = await generateAndStoreCode("watcherCode");
  if (watcherCodeResult.status != 200) {
    return "Error generating code";
  }
  return watcherCodeResult.code;
};

/**
 * If the logged on user owns the current match, and that match
 * has not started yet, and they cancel it, and the opponent gets notified
 * and then opponent clicks okay, then and only then will this completely delete the match
 * as if it never existed
 * @return {{status:number, message:string}} return object has two keys
 * * status is 200, 400
 * * message is the error message when status is not 200
 */
const cancel = async () => {
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = matchResult.currentMatch;
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session  expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (
    !(
      pAUserID === match.p1UserID ||
      pAUserID === match.p2UserID ||
      pAUserID === match.ownerUserID
    )
  ) {
    return {
      status: 400,
      message:
        "This final step to cancel can only happen if the logged on user is one of the players or the owner",
    };
  }

  let index = 0;
  for (const match of matchState.cache) {
    if (match.matchID === matchState.currentMatchID) {
      break;
    }
    index++;
  }

  matchState.cache.splice(index, 1);
  matchState.currentMatchID = "notSet";
  let response;
  try {
    const resource = "/v1/match";
    const body = JSON.stringify({
      idempotencyKey: idem.generateKey("match"),
      action: "cancel",
      matchID: match.matchID,
      pAUserID: pAUserID,
    });
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
      body: body,
    });
    if (!response.ok) {
      throw new MatchServiceError(`Response status: ${response.status}`);
    }
  } catch (error) {
    const statusCode = error.status || error.statusCode || 500;
    console.log(`Error (${statusCode}): ${error.message}`);

    return {
      status: statusCode,
      message: error.message,
      match: {},
    };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });

  return { status: 200, message: "" };
};

/**
 * If the logged on user is a owner in the current match,
 * and if the match has not started,
 * and they clicked to cancel the match,
 * this will notify their opponent
 */
const notifyCancel = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "pending") {
    return {
      status: 400,
      message:
        "You can't cancel when the match is in this state " + match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (pAUserID != match.ownerUserID) {
    return {
      status: 400,
      message: "Only the the match owner can cancel a match",
    };
  }
  let resultCancel;
  if (
    match.p1UserID === "waiting" ||
    match.p1UserID === COMPUTER_USERID ||
    match.p2UserID === "waiting" ||
    match.pwUserID === COMPUTER_USERID
  ) {
    resultCancel = await cancel();
  } else {
    resultCancel = await setMatchState("notifyCancel");
  }

  if (resultCancel.status != 200) {
    return resultCancel;
  }
  return { status: 200, message: "" };
};

/**
 * Removes the playingAs identity from watchers on the current match
 * @param {}
 * @return {{status:number, message:string}} return object has two keys
 * * status is 200, 400, 404
 * * message is the error message when status is not 200
 */
const stopWatching = async () => {
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = matchResult.currentMatch;
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return {
      status: pAIdentityResponse.status,
      message: "The session has expired. Try logging in again.",
    };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const watchers = match?.watchers ?? [];
  if (watchers.length === 0) {
    return { status: 404, message: "No one is watching this match" };
  }
  if (!match.watchers.includes(pAUserID)) {
    return {
      status: 404,
      message: "You are not watching this match",
    };
  }

  let response;
  try {
    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "removeWatcher",
      pAUserID: pAUserID,
      matchID: match.matchID,
      watcherID: pAUserID,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    const updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in stopWatching or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, message: error.message };
  }

  return { status: 200, message: "" };
};

/**
 * removes a player from the current match
 * @param {string} userID
 * @return {{status:number, message:string}} return object has two keys
 * * status is 200, 400, 404
 * * message is the error message when status is not 200
 */
const removePlayer = async (userID) => {
  if (!identityService.isUserIDValid(userID)) {
    return { status: 400, message: "The userID is not valid" };
  }
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = matchResult.currentMatch;
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  // if (pAUserID != match.ownerUserID) {
  //   return {
  //     status: 400,
  //     message: "Only the the match owner can remove a player",
  //   };
  // }
  if (match.ownerUserID === userID) {
    return { status: 400, message: "The owner can't be removed" };
  }
  if (match.state != "pending" && pAUserID === match.ownerUserID) {
    return {
      status: 400,
      message:
        "Owners can only be remove players when status is pending, but the state is: " +
        match.status,
    };
  }

  const moves = match?.moves ?? [];
  for (const move of moves) {
    if (move.player === userID) {
      // if the userID has made any moves, then they don't get a withdraw button
      return {
        status: 400,
        message:
          "Players can only withdraw when they haven't made any moves in a match they don't own.",
      };
    }
  }

  if (!(match.p1UserID === userID || match.p2UserID === userID)) {
    return { status: 400, message: "This userID is not a player: " + userID };
  }

  let response;
  try {
    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "removePlayer",
      pAUserID: pAUserID,
      matchID: match.matchID,
      userID: userID,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    const updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in removePlayer or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, message: error.message };
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * and if the match is in started,
 * this will resign them from the match and notify their opponent
 */
const notifyResign = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "started") {
    return {
      status: 400,
      message:
        "You can't resign when the match is in this state " + match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return { status: 400, message: "Only the players can resign" };
  }

  const resultSetState = await setMatchState("notifyResign");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * and if the match is in notifyResigned,
 * this will complete the resign process
 */
const completeResign = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "notifyResign") {
    return {
      status: 400,
      message:
        "You can't complete the resign process for the match is in this state " +
        match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return { status: 400, message: "Only the players can resign" };
  }
  const resultSetState = await setMatchState("finished");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * this will cause them to offer a draw to their
 * opponent
 */
const offerDraw = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return { status: 400, message: "Only the players can offer a draw" };
  }
  const resultSetState = await setMatchState("drawOffered");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * and their opponed offered a draw,
 * this will cause them to accept the draw
 */
const acceptDraw = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "drawOffered") {
    return {
      status: 400,
      message:
        "You can't accept a draw when the match is in this state " +
        match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return { status: 400, message: "Only the players can accept a draw" };
  }
  const resultSetState = await setMatchState("draw");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * and their opponent offered a draw
 * this will cause them to decline the offer
 */
const declineDraw = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "drawOffered") {
    return {
      status: 400,
      message:
        "You can't decline a draw when the match is in this state " +
        match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return { status: 400, message: "Only the players can decline a draw" };
  }
  const resultSetState = await setMatchState("drawDeclined");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is a player in the current match,
 * and their opponent offered a draw that was declined
 * this will cause the match to resume
 */
const resumeAfterDeclineDraw = async () => {
  const result = getCurrentMatch();
  if (result.status != 200) {
    return { status: 400, message: "There is no current match" };
  }
  const match = result.currentMatch;
  if (match.state != "drawDeclined") {
    return {
      status: 400,
      message:
        "You can't resume from a draw when the match is in this state " +
        match.state,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!(pAUserID === match.p1UserID || pAUserID === match.p2UserID)) {
    return {
      status: 400,
      message: "Only the players can resume after declining a draw",
    };
  }
  const resultSetState = await setMatchState("started");

  if (resultSetState.status != 200) {
    return resultSetState;
  }
  return { status: 200, message: "" };
};

/**
 * If the logged on user is an owner or player in the current match,
 * and the input userID is a watcher, then this will remove that
 * userID from the watchers.
 * @param {*} userID
 * @returns {{status:number, message:string}} The return object is:
 * * status is 200, 400, or 404
 * * message is an error message if the status is not 200
 */
const removeWatcher = async (userID) => {
  // validation #1: is the format valid?
  if (!identityService.isUserIDValid) {
    return { status: 400, message: "The userID is not valid" };
  }
  let response;
  let updatedMatch;

  try {
    const result = getCurrentMatch();
    if (result.status != 200) {
      throw new MatchServiceError("Unable to find a current match", 404);
    }
    const currentMatch = result.currentMatch;

    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    const pAUserID = pAIdentity.userID;

    // validation #2: are they even in the watcher array?
    if (Array.isArray(currentMatch?.watchers)) {
      const isUserNotWatching = !currentMatch.watchers.includes(userID);
      if (isUserNotWatching) {
        return {
          status: 400,
          message: `The userID ${userID} is not a watcher on match ${currentMatch.matchID}`,
        };
      }
    }

    // validation #3: is the playingAs identity an owner or player?
    if (
      !(
        pAUserID === currentMatch.p1UserID ||
        pAUserID === currentMatch.p2UserID ||
        pAUserID === currentMatch.ownerUserID
      )
    ) {
      return {
        status: 400,
        message: "Only a player or owner can remove a watcher",
      };
    }

    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "removeWatcher",
      pAUserID: pAUserID,
      matchID: currentMatch.matchID,
      watcherID: userID,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in removeWatcher or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, error: error.message };
  }

  return { status: 200 };
};

/**
 * Returns the state of the current match in a user friendly format
 */
const getFriendlyState = (state) => {
  switch (state) {
    case "drawOffered":
      return "Draw Offered";
    case "drawDeclined":
      return "Draw Declined";
    case "notifyResign":
      return "Notified Resign";
    case "notifyCancel":
      return "Notified Cancel";
    case "abandoned":
      return "Abandoned";
    default:
      return state.charAt(0).toUpperCase() + state.substring(1).toLowerCase();
  }
};

const setForceRotate = (boolean) => {
  if (!typeof boolean === "boolean") {
    console.log(
      "ERROR: Attempt to set forceRotate to invalid value: " + boolean,
    );
  }
  const currentMatch = getCurrentMatch().currentMatch;
  currentMatch.forceRotate = boolean;
};

const getAction = (player) => {
  if (!isPlayerValid(player)) {
    console.log(
      "Error: attempted to get the action of an invalid player: " + player,
    );
    console.trace();
    return;
  }
  const currentMatch = getCurrentMatch().currentMatch;
  return player === "playerOne" ? currentMatch.p1Action : currentMatch.p2Action;
};

/**
 * returns the action direction of the player
 * @param {string} player e.g. "playerOne" or "playerTwo"
 * @returns {string} direction
 */
const getDirection = (player) => {
  if (!isPlayerValid(player)) {
    console.log(
      "Error: Attempted to get the direction of an invalid player: " + player,
    );
    return;
  }
  const currentMatch = getCurrentMatch().currentMatch;
  return player === "playerOne"
    ? currentMatch.p1Direction
    : currentMatch.p2Direction;
};

const setDirection = ({ player, direction }) => {
  if (!isPlayerValid(player)) {
    console.log(
      "Error: Attempted to set the direction of an invalid player: " + player,
    );
    return;
  }
  if (!DIRECTIONS.includes(direction)) {
    console.log(
      "Error: Attempted to set the direction to an invalid direction: " +
        direction,
    );
    return;
  }
  const currentMatch = getCurrentMatch().currentMatch;
  switch (player) {
    case "playerOne":
      currentMatch.p1Direction = direction;
      break;
    case "playerTwo":
      currentMatch.p2Direction = direction;
      break;
  }
  return;
};

const setAction = async ({ action, player }) => {
  if (!isPlayerValid(player)) {
    console.log(
      "Error: attempted to set the action of an invalid player: " + player,
    );
    return;
  }
  if (!isActionValid(action)) {
    console.log(
      "Error: attempted to set the action to an invalid value: " + action,
    );
    return;
  }
  const currentMatch = getCurrentMatch().currentMatch;
  if (currentMatch.forceRotate === true && action === "push") {
    // when in forceRotate mode, you can't change it to push
    // which is the whole point of foreRotate mode
    return;
  }

  const attribute2Set = player === "playerOne" ? "p1Action" : "p2Action";
  currentMatch[attribute2Set] = action;
};

/**
 * @brief Posts the current match object to the back end for storage in DynamoDB
 */

const saveMatch = async () => {
  // console.log("-----saveMatch:" + "--------");
  // console.log("The Initial timestamp is: " + new Date().toISOString());
  const getCurrentMatchResult = getCurrentMatch();
  if (getCurrentMatchResult.status != 200) {
    return { status: 400, message: "There is no current match" };
  }

  const currentMatch = getCurrentMatchResult.currentMatch;
  const pAIdentityResponse = await identityService.getPlayingAs();

  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  let response;
  try {
    const resource = "/v1/match";
    const body = JSON.stringify({
      idempotencyKey: idem.generateKey("match"),
      action: "saveMatch",
      match: currentMatch,
      pAUserID: pAIdentity.userID,
    });
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
      body: body,
    });
    if (!response.ok) {
      throw new MatchServiceError(`Error saving match: ,${response.status}`);
    }
    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);
    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    return { status: 200, message: "" };
  } catch (error) {
    console.error(
      `Error in saveMatch or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, message: error.message };
  }
};

const pAIsOwner = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return false;
  }
  const pAUserID = pAIdentityResponse.identity.userID;
  const currentMatchResult = getCurrentMatch();
  if (currentMatchResult.status != 200) {
    // can't watch something that's not there
    return false;
  }
  const currentMatch = currentMatchResult.currentMatch;
  if (pAUserID === currentMatch.ownerUserID) {
    return true;
  }
  return false;
};

const pAIsPlayer = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return false;
  }
  const pAUserID = pAIdentityResponse.identity.userID;
  const currentMatchResult = getCurrentMatch();
  if (currentMatchResult.status != 200) {
    // can't watch something that's not there
    return false;
  }
  const currentMatch = currentMatchResult.currentMatch;
  if (
    pAUserID === currentMatch.p1UserID ||
    pAUserID === currentMatch.p2UserID
  ) {
    return true;
  }

  return false;
};

const refreshCurrentMatch = async () => {
  let response;

  try {
    const result = getCurrentMatch();
    if (result.status != 200) {
      throw new MatchServiceError("Unable to find a current match", 404);
    }
    const currentMatch = result.currentMatch;

    const pAIdentityResponse = await identityService.getPlayingAs();
    if (pAIdentityResponse.status != 200) {
      throw new MatchServiceError("The pAIdentity session has expired", 401);
    }
    const pAIdentity = pAIdentityResponse.identity;
    const pAUserID = pAIdentity.userID;

    const resource = "/v1/match";
    const body = {
      idempotencyKey: idem.generateKey("match"),
      action: "refreshCurrentMatch",
      pAUserID: pAUserID,
      matchID: currentMatch.matchID,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new MatchServiceError(
        `Error posting to ${resource}:`,
        response.status,
      );
    }

    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    const identityToken = bodyAsJSON.identityToken;
    const refreshToken = bodyAsJSON.refreshToken;
    await identityService.updatePlayingAsTokens({
      identityToken,
      refreshToken,
    });
    const updatedMatch = bodyAsJSON.match;
    updateMatchInCache(updatedMatch);
  } catch (error) {
    console.error(
      `Error in refreshcurrentmatch or helper functions ${error.status || 500}: ${
        error.message
      }`,
    );
    return { status: error.status || 500, error: error.message };
  }
};

const updateMatchInCache = (updatedMatch) => {
  if (TROUBLESHOOTING) {
    console.log("updateMatchInCache: Attempting to update match: " + updatedMatch.matchID);
    console.log("updateMatchInCache: Match data pieces is: " + updatedMatch.pieces + " (type: " + typeof updatedMatch.pieces + ")");
    console.log("updateMatchInCache: Match data turn is: " + updatedMatch.turn + " (type: " + typeof updatedMatch.turn + ")");
    console.log("updateMatchInCache: Match state is: " + updatedMatch.state);
  }
  // Detect if this is a partial match (from GSI) or complete match (from main table)
  // Partial matches are missing key fields like 'state', 'pieces', 'moves'
  const keys = Object.keys(updatedMatch);
  const isPartial = !keys.includes("state") || !keys.includes("pieces") || !keys.includes("moves");
  
  // Validate the match first - if validation fails, skip this match
  const validationResult = isMatchValid(updatedMatch, isPartial);
  if (validationResult.status !== 200) {
    if (TROUBLESHOOTING) {
      console.log("updateMatchInCache: Validation failed: " + validationResult.message);
    }
    return;
  }
  if (TROUBLESHOOTING) {
    console.log("updateMatchInCache: Validation passed");
  }

  // Validate and sanitize message if present
  const keysAfterValidation = Object.keys(updatedMatch);
  if (keysAfterValidation.includes("message")) {
    // message is optional
    if (typeof updatedMatch.message !== "string") {
      console.log("ERROR: The message is not a string, removing it");
      delete updatedMatch.message;
    } else if (updatedMatch.message.length > MESSAGE_MAX_LENGTH) {
      console.log("ERROR: The message is too long, removing it");
      delete updatedMatch.message;
    } else {
      // Sanitize message on load with the shared filter: forbidden characters
      // and banned words/phrases are replaced with "..."
      updatedMatch.message = sanitizeText(updatedMatch.message);
    }
  }

  const index = matchState.cache.findIndex(
    (match) => match.matchID === updatedMatch.matchID,
  );

  if (index != -1) {
    // A partial match (from the loadMatches GSI) only has a few keys. Don't
    // let it wipe out a complete cached match: keep the complete copy and
    // overlay just the fields the partial one has. Otherwise p1Name, pieces,
    // etc. disappear (e.g. after renaming while a match is open).
    matchState.cache[index] = isPartial
      ? { ...matchState.cache[index], ...updatedMatch }
      : updatedMatch;
  }

  if (index === -1) {
    matchState.cache.push(updatedMatch);
  }
};

const getCache = () => {
  return JSON.parse(JSON.stringify(matchState.cache));
};

/**
 * Drops a match from the local cache, e.g. after the playing as identity was
 * removed as a watcher. If it was the current match, there's no current
 * match afterward (call loadPlayingAsMatches to pick another).
 * @param {string} matchID
 */
const removeMatchFromCache = (matchID) => {
  matchState.cache = matchState.cache.filter((match) => match.matchID !== matchID);
  if (matchState.currentMatchID === matchID) {
    matchState.currentMatchID = "noCurrentMatch";
  }
};

export const matchService = {
  removeMatchFromCache,
  acceptDraw,
  cancel,
  changeTurn,
  create,
  completeResign,
  declineDraw,
  generatePlayerCodeP1,
  generatePlayerCodeP2,
  generateWatcherCode,
  getCurrentMatch,
  getAction,
  getDirection,
  loadMatches,
  getCurrentMatchID,
  getFriendlyState,
  getCache,
  getPieces,
  getScore,
  getTurn,
  isCoordsValid,
  isDirectionValid,
  isModeValid,
  isPATurn,
  isPlayerValid,
  isJoinCodeValid,
  isMatchIDValid,
  join,
  loadPlayingAsMatches,
  notifyResign,
  notifyCancel,
  offerDraw,
  pAIsPlayer,
  pAIsOwner,
  refreshCurrentMatch,
  removePiece,
  removePlayer,
  removeWatcher,
  resumeAfterDeclineDraw,
  setAction,
  setCurrentMatchID,
  setDirection,
  setForceRotate,
  setMatchState,
  setScore,
  stopWatching,
  updateUserName,
  addPiece,
  updatePiece,
  updateMatchInCache,
};
